import { defineStore } from "pinia";
import _ from "lodash";
import * as _debug from "debug";
import ApiClient from "@/lib/ApiClient";
import { objectDiff, sureClone } from "@/lib/DataUtils";
import { createStore } from "./storeHelper";

const debug = _debug(`${process.env.VUE_APP_PREFIX}:ChecklistsStore`);

const DEFAULT_EDITOR = {
  id: null,
  name: null,
  isCrossCheckList: false,
  isInformationOnly: false,
  categoryId: null,
};

export const useChecklistsStore = defineStore(
  "checklists",
  createStore({
    state: {
      checklists: {},
      editor: sureClone(DEFAULT_EDITOR),
    },

    getters: {
      checklistsList: (state) => {
        return _.values(state.checklists);
      },
      checklistById: (state) => (id) => {
        return _.values(state.checklists).find((c) => c.id === id);
      },
      editorChanges: (state) => {
        const id = _.get(state, "editor.id");
        if (!id) {
          return null;
        }
        const original = _.get(state.checklists, id);
        if (!original) {
          return null;
        }
        return objectDiff(original, state.editor);
      },
      editorHasChanges() {
        const diff = this.editorChanges;
        return diff && !_.isEmpty(diff);
      },
    },

    actions: {
      addChecklist(checklist) {
        this.checklists[checklist.id] = checklist;
      },

      deleteChecklist(checklistId) {
        delete this.checklists[checklistId];
      },

      clearChecklists() {
        this.checklists = {};
      },

      async load() {
        debug("load");
        const checklists = await ApiClient.get(`/checklists`);
        debug("load", checklists);
        this.set("checklists", _.keyBy(checklists, "id"));
      },

      async loadChecklistToEditor(checklistId) {
        debug("loadChecklistToEditor", checklistId);
        const checklist = _.get(this.checklists, checklistId);
        debug("loadChecklistToEditor", checklist);
        if (!checklist) {
          throw new Error("Checklist not found");
        }
        this.set("editor", sureClone(checklist));
      },

      async saveChecklistInEditor() {
        const checklistId = this.editor.id;
        if (!this.editorHasChanges) {
          debug("saveChecklistInEditor", "Ei tallennettavia muutoksia.");
          return;
        }
        const modifiedChecklist = sureClone(this.editor);
        debug("saveChecklistInEditor", checklistId, modifiedChecklist);
        const result = await ApiClient.put(
          `/checklists/${checklistId}`,
          modifiedChecklist,
        );
        debug("saveChecklistInEditor, result", result);

        if (result) {
          this.addChecklist(result);

          // Import other stores when needed
          const { useTemplatesStore } = await import("./admin/templates");
          const { useCatalogStore } = await import("./catalog");

          const templatesStore = useTemplatesStore();
          const catalogStore = useCatalogStore();

          await templatesStore.syncChangesInTarget(result);
          await catalogStore.syncChangesInTarget(result);
        }
      },
    },
  }),
);
